import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import Modal from './Modal'
import Boton from './Boton'
import { importacionPreseaApi } from '../api/notasVenta'
import { fmtFecha, fmtMonto } from '../utils/fecha'
import { colors, font, text, space, radius, sx } from '../design/tokens'

/**
 * "Importar desde Presea" (plan B, Martin 7 sep 2026): lo que se facturó A
 * MANO en Presea un día dado, agrupado por NV de Presea (factura + remito +
 * recibo del mismo cliente), para traerlo a LGR como NV procesada lista para
 * avisar al cliente y pasar a logística.
 *
 * El backend decide qué ve cada uno (admin: todo; supervisor: todo menos el
 * vendedor 22; ventas: lo suyo) y NO devuelve lo que ya está en LGR. El filtro
 * por vendedor viaja al servidor (`?vendedor=`); el selector lista los
 * vendedores del día que este usuario puede ver.
 */
const hoyIso = () => new Date().toISOString().slice(0, 10)

// El mismo marcador "en curso" del stepper del paquete en la NV
// (NotaVentaDetallePage → PasoChain): aro azul girando.
function Spinner({ size = 22 }) {
  return (
    <span aria-hidden style={{
      flexShrink: 0, width: size, height: size, borderRadius: radius.pill, display: 'inline-block',
      background: colors.linkBg, border: `2px solid ${colors.link}`, borderTopColor: 'transparent',
      boxSizing: 'border-box', animation: 'lgr-spin 0.8s linear infinite',
    }} />
  )
}

const ESTADO = {
  importable:    { label: 'Para importar', color: colors.ok,      bg: colors.okBg },
  bloqueada:     { label: 'Bloqueada',     color: colors.err,     bg: colors.errBg },
  no_importable: { label: 'No es venta',   color: colors.textSec, bg: colors.bgSoft },
}

export default function ModalImportarPresea({ onClose, onImportado }) {
  const [fecha, setFecha] = useState(hoyIso())
  const [vendedor, setVendedor] = useState('')
  const [previa, setPrevia] = useState(null)
  const [cargando, setCargando] = useState(false)
  const [error, setError] = useState(null)
  const [sel, setSel] = useState(() => new Set())
  const [importando, setImportando] = useState(false)
  const [resultados, setResultados] = useState(null)
  const [hover, setHover] = useState(null)

  const cargar = async (f, v) => {
    setCargando(true); setError(null); setResultados(null); setSel(new Set())
    try {
      const { data } = await importacionPreseaApi.previa(f, v || undefined)
      setPrevia(data)
    } catch (err) {
      setPrevia(null)
      setError(err.response?.data?.errors ? Object.values(err.response.data.errors).flat().join(' · ') : (err.response?.data?.message || 'No se pudo consultar Presea.'))
    } finally { setCargando(false) }
  }
  // Consulta al abrir y al cambiar fecha o vendedor (diferida: sin setState
  // sincrónico dentro del efecto).
  useEffect(() => {
    const t = setTimeout(() => cargar(fecha, vendedor), 0)
    return () => clearTimeout(t)
  }, [fecha, vendedor])

  const operaciones = useMemo(() => previa?.operaciones || [], [previa])
  const importables = useMemo(() => operaciones.filter(o => o.estado === 'importable'), [operaciones])
  const nombreVendedor = (cod) => previa?.vendedores?.find(x => x.codigo === cod)?.nombre || null

  const toggle = (nota) => setSel(prev => { const n = new Set(prev); n.has(nota) ? n.delete(nota) : n.add(nota); return n })
  const todasTildadas = importables.length > 0 && importables.every(o => sel.has(o.nota))
  const toggleTodas = () => setSel(todasTildadas ? new Set() : new Set(importables.map(o => o.nota)))

  const importar = async () => {
    if (!sel.size) return
    setImportando(true); setError(null)
    try {
      const { data } = await importacionPreseaApi.importar(fecha, [...sel])
      setResultados(data.resultados)
      onImportado?.()
    } catch (err) {
      setError(err.response?.data?.message || 'La importación falló.')
    } finally { setImportando(false) }
  }

  const okCount = resultados?.filter(r => r.ok).length ?? 0
  // Remito/recibo: el primero a la vista, el resto como "+N" (todos en el title).
  const varios = (lista) => {
    if (!lista.length) return <span style={sx.placeholder}>—</span>
    return (
      <span title={lista.map(x => x.numero).join('\n')}>
        {lista[0].numero}
        {lista.length > 1 && <div style={st.sub}>+{lista.length - 1}</div>}
      </span>
    )
  }

  return (
    <Modal title="Importar desde Presea" onClose={onClose} maxWidth="1200px">
      <style>{'@keyframes lgr-spin { to { transform: rotate(360deg); } }'}</style>
      <p style={st.intro}>
        Ventas hechas <strong>a mano en Presea</strong>, agrupadas por su NV (factura + remito + recibo).
        Cada una entra a LGR como NV procesada, con su remito de logística y su recibo. Lo que ya está en LGR no aparece.
      </p>

      <div style={st.barra}>
        <label style={st.campo}>
          <span style={st.label}>Fecha</span>
          <input type="date" value={fecha} max={hoyIso()} onChange={e => setFecha(e.target.value)} style={sx.input} />
        </label>
        {(previa?.ve_todo || previa?.elige_vendedor) && (
          <label style={st.campo}>
            <span style={st.label}>Vendedor</span>
            <select value={vendedor} onChange={e => setVendedor(e.target.value)} style={{ ...sx.input, minWidth: 220 }}>
              <option value="">Todos</option>
              {(previa.vendedores || []).map(v => (
                <option key={v.codigo} value={v.codigo}>{v.codigo}{v.nombre ? ` · ${v.nombre}` : ''}</option>
              ))}
            </select>
          </label>
        )}
        <div style={{ flex: 1 }} />
        {!cargando && previa && !resultados && (
          <span style={st.resumen}>
            {importables.length === 0 ? 'Nada para importar' : `${importables.length} para importar`}
            {operaciones.length > importables.length ? ` · ${operaciones.length - importables.length} sin poder importar` : ''}
          </span>
        )}
        <Boton variante="ghost" sm type="button" onClick={() => cargar(fecha, vendedor)} disabled={cargando}>↻ Actualizar</Boton>
      </div>

      {error && <div style={st.error}>{error}</div>}

      <div style={st.marco}>
        {cargando ? (
          <div style={st.procesando}>
            <Spinner />
            <div>
              <div style={{ color: colors.text, fontWeight: 600 }}>Consultando Presea…</div>
              <div style={st.sub}>Trayendo los comprobantes y sus renglones del {fmtFecha(fecha)}.</div>
            </div>
          </div>
        ) : operaciones.length === 0 ? (
          <p style={st.vacio}>No hay ventas hechas a mano en Presea el {fmtFecha(fecha)}{vendedor ? ' para ese vendedor' : ''}.</p>
        ) : (
          <table style={sx.table}>
            <thead>
              <tr>
                <th style={{ ...sx.th, width: 36 }}>
                  {!resultados && importables.length > 0 && (
                    <input type="checkbox" checked={todasTildadas} onChange={toggleTodas} aria-label="Tildar todas las importables" />
                  )}
                </th>
                <th style={sx.th}>NV Presea</th>
                <th style={{ ...sx.th, minWidth: 180 }}>Cliente</th>
                <th style={{ ...sx.th, minWidth: 130 }}>Vendedor</th>
                <th style={sx.th}>Factura</th>
                <th style={sx.th}>Remito</th>
                <th style={sx.th}>Recibo</th>
                <th style={{ ...sx.th, textAlign: 'right' }}>Reng.</th>
                <th style={{ ...sx.th, textAlign: 'right' }}>Total</th>
                <th style={{ ...sx.th, minWidth: 210 }}>Estado</th>
              </tr>
            </thead>
            <tbody>
              {operaciones.map(o => {
                const e = ESTADO[o.estado] || ESTADO.no_importable
                const res = resultados?.find(r => r.nota === o.nota)
                const tildable = o.estado === 'importable' && !resultados
                const apagada = o.estado !== 'importable' && !res
                const seleccionada = sel.has(o.nota)
                const textoCls = apagada ? st.principalApagado : st.principal
                return (
                  <tr key={o.nota}
                    style={{ ...st.fila, cursor: tildable ? 'pointer' : 'default',
                      background: seleccionada ? colors.linkBg : (tildable && hover === o.nota ? colors.bgSoft : undefined) }}
                    onMouseEnter={() => tildable && setHover(o.nota)} onMouseLeave={() => setHover(null)}
                    onClick={() => tildable && toggle(o.nota)}>
                    <td style={st.td} onClick={e => e.stopPropagation()}>
                      {tildable && <input type="checkbox" checked={sel.has(o.nota)} onChange={() => toggle(o.nota)} aria-label={`Importar NV ${o.nota}`} />}
                    </td>
                    <td style={{ ...st.td, ...sx.mono, fontWeight: 500 }}>{o.nota}</td>
                    <td style={st.td}>
                      <div style={textoCls}>{o.cliente_nombre || <span style={sx.placeholder}>Sin ficha en LGR</span>}</div>
                      <div style={{ ...st.sub, ...sx.mono }}>{o.cliente_codigo}</div>
                    </td>
                    <td style={st.td}>
                      <div style={textoCls}>{nombreVendedor(o.vendedor) || <span style={sx.placeholder}>—</span>}</div>
                      <div style={{ ...st.sub, ...sx.mono }}>{o.vendedor}</div>
                    </td>
                    <td style={{ ...st.td, ...sx.mono }}>
                      {o.factura ? <>{o.factura.numero}<div style={st.sub}>{o.factura.letra ? `Factura ${o.factura.letra}` : 'Factura'}</div></> : <span style={sx.placeholder}>—</span>}
                    </td>
                    <td style={{ ...st.td, ...sx.mono }}>{varios(o.remitos)}</td>
                    <td style={{ ...st.td, ...sx.mono }}>{varios(o.recibos)}</td>
                    <td style={{ ...st.td, ...sx.mono, textAlign: 'right' }}
                      title={o.renglones.map(r => `${r.cantidad} × ${r.codigo_alfa} ${r.detalle} — $ ${fmtMonto(r.precio_unitario_con_iva)}`).join('\n')}>
                      {o.renglones.length}
                    </td>
                    <td style={{ ...st.td, ...sx.mono, textAlign: 'right', whiteSpace: 'nowrap', fontWeight: 600 }}>{o.factura ? `$ ${fmtMonto(o.factura.importe)}` : <span style={sx.placeholder}>—</span>}</td>
                    <td style={st.td}>
                      {importando && sel.has(o.nota) ? (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: space.md }}>
                          <Spinner size={18} />
                          <span style={{ fontSize: text.sec, color: colors.link, fontWeight: 600 }}>Procesando…</span>
                        </span>
                      ) : res ? (
                        res.ok
                          ? <><span style={{ ...st.badge, color: colors.ok, background: colors.okBg }}>Importada</span>
                              <div style={st.sub}><Link to={`/notas-venta/${res.nota_venta_id}`} style={st.link}>{res.numero_lgr}</Link></div></>
                          : <><span style={{ ...st.badge, color: colors.err, background: colors.errBg }}>No importada</span>
                              <div style={st.sub}>{res.error}</div></>
                      ) : (
                        <span style={{ ...st.badge, color: e.color, background: e.bg }}>{e.label}</span>
                      )}
                      {res?.avisos?.length > 0 && <div style={st.motivo} title={res.avisos.join('\n')}>{res.avisos.join(' · ')}</div>}
                      {!res && o.motivos?.length > 0 && <div style={st.motivo} title={o.motivos.join('\n')}>{o.motivos.join(' · ')}</div>}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </div>

      <div style={st.acciones}>
        {resultados && <span style={st.resumen}>{okCount} de {resultados.length} importadas</span>}
        <div style={{ flex: 1 }} />
        <Boton variante="ghost" type="button" onClick={onClose}>{resultados ? 'Cerrar' : 'Cancelar'}</Boton>
        {!resultados && (
          <Boton variante="primary" type="button" onClick={importar} disabled={importando || sel.size === 0}
            title="Crea en LGR una NV procesada por cada operación tildada, con su remito y su recibo">
            {importando ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: space.sm }}><Spinner size={14} /> Importando…</span> : sel.size ? `Importar ${sel.size}` : 'Importar'}
          </Boton>
        )}
      </div>
    </Modal>
  )
}

const st = {
  intro: { margin: `0 0 ${space.lg}px`, color: colors.textSec, fontSize: text.sec, lineHeight: 1.45 },
  barra: { display: 'flex', alignItems: 'flex-end', gap: space.lg, flexWrap: 'wrap', marginBottom: space.lg },
  campo: { display: 'flex', flexDirection: 'column', gap: space.xs },
  label: { fontSize: text.meta, fontWeight: 600, color: colors.textSec, textTransform: 'uppercase', letterSpacing: '0.05em' },
  resumen: { fontSize: text.sec, color: colors.textSec },
  error: { background: colors.errBg, color: colors.err, padding: `${space.md}px ${space.lg}px`, borderRadius: radius.sm, fontSize: text.body, marginBottom: space.lg },
  marco: { border: `1px solid ${colors.border}`, borderRadius: radius.md, overflow: 'auto', maxHeight: '55vh' },
  procesando: { display: 'flex', alignItems: 'center', gap: space.lg, padding: `${space['3xl']}px`, justifyContent: 'center', fontSize: text.body },
  vacio: { margin: 0, padding: `${space['3xl']}px`, textAlign: 'center', color: colors.textMeta, fontSize: text.body },
  fila: { borderBottom: `1px solid ${colors.borderSoft}` },
  td: { ...sx.td, padding: `${space.sm}px ${space.xl}px`, verticalAlign: 'middle', lineHeight: 1.3 },
  badge: { ...sx.badge, whiteSpace: 'nowrap' },
  principal: { color: colors.text },
  principalApagado: { color: colors.textMeta },
  motivo: { fontSize: text.sec, color: colors.textSec, marginTop: 2, maxWidth: 260, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  sub: { fontSize: text.sec, color: colors.textMeta, marginTop: 2 },
  link: { color: colors.link, textDecoration: 'none', fontFamily: font.mono },
  acciones: { display: 'flex', alignItems: 'center', gap: space.md, marginTop: space.xl },
}
